/**
 * Procurements Dashboard
 *
 * Main dashboard for Procurement Management domain.
 * Shows role-aware stats, quick actions, recent activity, and pipeline overview.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconFileText,
  IconSend,
  IconChecks,
  IconCircleCheck,
  IconChecklist,
  IconCash,
  IconPlus,
  IconArrowRight,
  IconRefresh,
} from '@tabler/icons-react';
import {
  Stack,
  Card,
  Grid,
  Group,
  Badge,
  Button,
  Text,
  Title,
  ThemeIcon,
  Skeleton,
  Table,
  SimpleGrid,
  Paper,
  Tooltip,
} from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { RequestStatusBadge } from '../components';
import {
  useProcurementDashboardService,
  usePurchaseRequestService,
} from '@actschurch/ccms-sdk';

// Helper to parse decimal timestamp (yyyyMMddHHmmss) to readable date
const formatDate = (dt: number | undefined): string => {
  if (!dt) return '-';
  const str = String(dt);
  if (str.length < 8) return '-';
  const year = str.substring(0, 4);
  const month = str.substring(4, 6);
  const day = str.substring(6, 8);
  return `${day}/${month}/${year}`;
};

export default function ProcurementsDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dashboardService = useProcurementDashboardService();
  const purchaseRequestService = usePurchaseRequestService();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Fetch dashboard stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['procurements', 'dashboard', 'stats'],
    queryFn: () => dashboardService.getMyStats(),
  });

  // Fetch user's roles
  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['procurements', 'dashboard', 'roles'],
    queryFn: () => dashboardService.getMyRoles(),
  });

  // Fetch recent requests for activity table
  const { data: recentRequests = [], isLoading: requestsLoading } = useQuery({
    queryKey: ['procurements', 'my-requests', 'recent'],
    queryFn: () => purchaseRequestService.getMyListView(),
  });

  // Refresh all dashboard data
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['procurements', 'dashboard', 'stats'] }),
      queryClient.invalidateQueries({ queryKey: ['procurements', 'dashboard', 'roles'] }),
      queryClient.invalidateQueries({ queryKey: ['procurements', 'my-requests', 'recent'] }),
    ]);
    setLastRefresh(new Date());
    setIsRefreshing(false);
  };

  const formatLastRefresh = (date: Date | null) => {
    if (!date) return null;
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Recent activity — last 10 requests sorted by newest first
  const activityList = useMemo(() => {
    return [...recentRequests]
      .sort((a, b) => (b.createdDateTime || 0) - (a.createdDateTime || 0))
      .slice(0, 10);
  }, [recentRequests]);

  // Pipeline counts by status
  const pipeline = useMemo(() => {
    const counts = { draft: 0, submitted: 0, lineApproved: 0, financeApproved: 0, ordered: 0, completed: 0 };
    recentRequests.forEach((r) => {
      if (r.statusID === 0) counts.draft++;
      else if (r.statusID === 1) counts.submitted++;
      else if (r.statusID === 2) counts.lineApproved++;
      else if (r.statusID === 3) counts.financeApproved++;
      else if (r.statusID >= 4 && r.statusID < 10) counts.ordered++;
      else if (r.statusID >= 10) counts.completed++;
    });
    return counts;
  }, [recentRequests]);

  const isLoading = statsLoading || rolesLoading || requestsLoading;

  if (isLoading) {
    return (
      <FadeIn>
        <Stack gap="lg">
          <Skeleton height={40} width={300} />
          <SimpleGrid cols={{ base: 2, md: 4 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={120} />
            ))}
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Skeleton height={300} />
            <Skeleton height={300} />
          </SimpleGrid>
        </Stack>
      </FadeIn>
    );
  }

  const statCards = [
    {
      label: 'My Drafts',
      value: stats?.myDrafts ?? 0,
      icon: <IconFileText size={22} />,
      color: 'gray',
      visible: true,
    },
    {
      label: 'Submitted',
      value: stats?.mySubmitted ?? 0,
      icon: <IconSend size={22} />,
      color: 'blue',
      visible: true,
    },
    {
      label: 'Approved',
      value: stats?.myApproved ?? 0,
      icon: <IconChecks size={22} />,
      color: 'green',
      visible: true,
    },
    {
      label: 'Completed',
      value: stats?.myCompleted ?? 0,
      icon: <IconCircleCheck size={22} />,
      color: 'teal',
      visible: true,
    },
    {
      label: 'Pending Approvals',
      value: stats?.pendingLineApprovals ?? 0,
      icon: <IconChecklist size={22} />,
      color: 'orange',
      visible: stats?.isLineApprover || stats?.isAdmin,
    },
    {
      label: 'Pending Finance',
      value: stats?.pendingFinanceApprovals ?? 0,
      icon: <IconCash size={22} />,
      color: 'pink',
      visible: stats?.isFinanceOfficer || stats?.isAdmin,
    },
  ];

  const visibleCards = statCards.filter((c) => c.visible);

  return (
    <FadeIn>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>Procurements Dashboard</Title>
            <Text size="sm" c="dimmed">
              Manage procurement requests and orders
            </Text>
          </div>
          <Group gap="sm">
            <Tooltip label={lastRefresh ? `Last refreshed: ${formatLastRefresh(lastRefresh)}` : 'Refresh data'}>
              <Button
                variant="subtle"
                size="sm"
                leftSection={<IconRefresh size={16} />}
                loading={isRefreshing}
                onClick={handleRefresh}
              >
                Refresh
              </Button>
            </Tooltip>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => navigate('/app/procurements/requests/create-request')}
            >
              New Request
            </Button>
          </Group>
        </Group>

        {/* Stat Cards */}
        <SimpleGrid cols={{ base: 2, sm: 3, md: visibleCards.length > 4 ? 3 : 4, lg: visibleCards.length }}>
          {visibleCards.map((card) => (
            <Card key={card.label} withBorder p="lg">
              <Stack gap="xs">
                <Group justify="space-between">
                  <ThemeIcon variant="light" color={card.color} size="lg">
                    {card.icon}
                  </ThemeIcon>
                  <Badge variant="light" color={card.color}>
                    {card.label}
                  </Badge>
                </Group>
                <Text size="xl" fw={700}>
                  {card.value}
                </Text>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>

        {/* Quick Actions */}
        <Group gap="sm">
          <Button
            variant="light"
            size="sm"
            rightSection={<IconArrowRight size={14} />}
            onClick={() => navigate('/app/procurements/requests/my-requests')}
          >
            View My Requests
          </Button>
          {(stats?.isLineApprover || stats?.isAdmin) && (
            <Button
              variant="light"
              color="orange"
              size="sm"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('/app/procurements/approvals/pending')}
            >
              Pending Approvals
            </Button>
          )}
          {(stats?.isFinanceOfficer || stats?.isAdmin) && (
            <Button
              variant="light"
              color="pink"
              size="sm"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('/app/procurements/approvals/pending')}
            >
              Finance Queue
            </Button>
          )}
        </Group>

        <Grid>
          {/* Recent Activity */}
          <Grid.Col span={{ base: 12, lg: 7 }}>
            <Card withBorder p="lg" h="100%">
              <Group justify="space-between" mb="md">
                <Title order={4}>Recent Activity</Title>
                <Button
                  variant="subtle"
                  size="xs"
                  rightSection={<IconArrowRight size={14} />}
                  onClick={() => navigate('/app/procurements/requests/my-requests')}
                >
                  View all
                </Button>
              </Group>
              {activityList.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  No recent requests found. Create your first request to get started.
                </Text>
              ) : (
                <Table tableVariant="striped">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Request</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Date</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {activityList.map((req) => (
                      <Table.Tr
                        key={req.requestID}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/app/procurements/requests/view/${req.requestID}`)}
                      >
                        <Table.Td>
                          <Text size="sm" fw={500} lineClamp={1}>
                            {req.name || `Request #${req.requestID}`}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {req.requestTypeName ? (
                            <Badge variant="light" color={req.requestTypeColor || 'gray'} size="sm">
                              {req.requestTypeName}
                            </Badge>
                          ) : (
                            <Text size="sm" c="dimmed">-</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <RequestStatusBadge status={req.statusID} />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">{formatDate(req.createdDateTime)}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Card>
          </Grid.Col>

          {/* Pipeline Overview */}
          <Grid.Col span={{ base: 12, lg: 5 }}>
            <Card withBorder p="lg" h="100%">
              <Title order={4} mb="md">Pipeline Overview</Title>
              <Stack gap="md">
                {[
                  { label: 'Draft', count: pipeline.draft, color: 'gray' },
                  { label: 'Submitted', count: pipeline.submitted, color: 'blue' },
                  { label: 'Line Approved', count: pipeline.lineApproved, color: 'cyan' },
                  { label: 'Finance Approved', count: pipeline.financeApproved, color: 'green' },
                  { label: 'Ordered / Invoicing', count: pipeline.ordered, color: 'violet' },
                  { label: 'Completed', count: pipeline.completed, color: 'teal' },
                ].map((stage) => (
                  <Paper key={stage.label} p="sm" withBorder>
                    <Group justify="space-between">
                      <Group gap="xs">
                        <ThemeIcon variant="light" color={stage.color} size="sm">
                          <Text size="xs" fw={700}>{stage.count}</Text>
                        </ThemeIcon>
                        <Text size="sm">{stage.label}</Text>
                      </Group>
                      <Badge variant="filled" color={stage.color} size="sm">
                        {stage.count}
                      </Badge>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>
    </FadeIn>
  );
}
